import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { createConnectQueryKey } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon, XIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  ensureWorker,
  listWorkers,
} from "@/gen/nodebase/v1/worker-WorkerService_connectquery";
import { getBillingStatus } from "@/gen/nodebase/v1/billing-BillingService_connectquery";
import { WorkerSetupProblem, WorkerState, type Worker } from "@/gen/nodebase/v1/worker_pb";
import { WorkerStorageProtection } from "@/gen/nodebase/v1/common_pb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CopyButton } from "@/components/copy";
import { EmptyNote, ErrorNote, Loading, PageHeader } from "@/components/page";
import { AccountScope } from "@/components/scope";
import { GettingStarted } from "@/components/getting-started";
import { WorkerSetupProgress } from "@/components/worker-setup";
import {
  SubscribeCard,
  SubscriptionNote,
  accessRunsWorker,
} from "@/components/worker-subscription";
import { ago, enumLabel, stateTone } from "@/lib/format";
import { setSession, useSession } from "@/lib/session";

// States a worker passes through on its own. While any worker is in one, the
// list polls: provisioning is the one flow here whose result arrives seconds
// later and out of band, and "click refresh until it says ready" is not an
// answer.
const TRANSIENT: WorkerState[] = [
  WorkerState.PROVISIONING,
  WorkerState.REGISTERING,
  WorkerState.REBUILDING,
];

// Polling cadence (docs/worker-setup-progress.md#how-changes-arrive). There is
// no push channel, so the page asks: every 3 s while a Worker is on its way,
// every 30 s while its setup has stopped (an operator may restart it), and the
// billing status every minute while the page is visible — a renewal that
// fails while the tab sits open shows up without a reload. Hidden tabs do not
// poll; returning to one refetches.
function workerPoll(rows: Worker[] | undefined): number | false {
  if (!rows) return false;
  if (rows.some((w) => w.setup && w.setup.problem !== WorkerSetupProblem.STOPPED)) return 3_000;
  if (rows.some((w) => TRANSIENT.includes(w.state))) return 3_000;
  if (rows.some((w) => w.setup)) return 30_000;
  return false;
}
const BILLING_POLL = 60_000;
// The subscription arrives by webhook, normally within seconds of payment
// (4 s on 2026-11-03). Past this the page says it is still waiting instead of
// repeating "confirming" indefinitely.
const CONFIRM_PATIENCE = 60_000;

// Worker lifecycle is server-managed (one per user): this page shows the
// worker and, when none exists yet, offers the one idempotent action —
// EnsureWorker. Create/destroy/rebuild are operator verbs and live in the
// admin fleet view, not here.
export function Workers() {
  const session = useSession();
  const queryClient = useQueryClient();

  const workers = useQuery(
    listWorkers,
    {},
    { refetchInterval: (query) => workerPoll(query.state.data?.workers) },
  );

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listWorkers,
        cardinality: undefined,
      }),
    });

  const ensure = useMutation(ensureWorker, { onSuccess: invalidate });

  // Returning from the hosted payment page. The subscription arrives by
  // webhook a moment later, so poll until it does, then start the Worker.
  const [params] = useSearchParams();
  const paid = params.get("checkout") === "success";
  const billing = useQuery(
    getBillingStatus,
    {},
    {
      refetchInterval: (query) =>
        paid && !accessRunsWorker(query.state.data?.access) ? 3_000 : BILLING_POLL,
    },
  );
  const [confirmSlow, setConfirmSlow] = useState(false);
  const confirming = paid && billing.isSuccess && !accessRunsWorker(billing.data?.access);
  useEffect(() => {
    if (!confirming) return;
    const id = window.setTimeout(() => setConfirmSlow(true), CONFIRM_PATIENCE);
    return () => window.clearTimeout(id);
  }, [confirming]);
  // An unreadable billing status falls back to offering setup: the server's
  // own gate answers EnsureWorker either way.
  const entitled = billing.isError || accessRunsWorker(billing.data?.access);

  const rows = workers.data?.workers ?? [];
  const onboardingStarted = useRef(false);

  // Registration commits Account + Workspace + placement first, then this
  // authenticated screen starts compute. Keeping provisioning out of the
  // registration transaction means a regional outage leaves a retryable
  // account, while this one-shot effect removes the old manual setup detour.
  useEffect(() => {
    if (
      (session?.newAccount || paid) &&
      workers.isSuccess &&
      billing.isSuccess &&
      entitled &&
      rows.length === 0 &&
      !onboardingStarted.current
    ) {
      onboardingStarted.current = true;
      ensure.mutate({});
    }
  }, [session?.newAccount, paid, workers.isSuccess, billing.isSuccess, entitled, rows.length, ensure]);

  return (
    <>
      <PageHeader title="Your Worker" scope={<AccountScope noun="Worker" />} />

      {session?.newAccount && (
        <div
          data-testid="welcome-banner"
          className="mb-4 flex items-start gap-3 rounded-md border bg-secondary px-4 py-3 text-sm"
        >
          <div className="flex-1">
            <p className="font-medium">Welcome — your account is ready.</p>
            <p className="text-muted-foreground">
              {workers.isError
                ? "Your account is ready, but we couldn't check your Worker. Retry the status check below."
                : billing.isSuccess && !entitled && rows.length === 0
                  ? paid
                    ? "Payment received. Your Worker starts as soon as the subscription is confirmed."
                    : billing.data?.billingEnabled && billing.data.slotAvailable
                    ? "Your account is ready. Subscribe below to start your Worker."
                    : "Your account is ready. See below for how to get a Worker."
                  : ensure.isError
                    ? "Your account is ready. Worker setup did not finish; retry below."
                    : rows.some((w) => w.state === WorkerState.READY)
                      ? "Your Worker is ready. Next, connect your computer using Desktop."
                      : ensure.isPending ||
                          rows.some(
                            (w) =>
                              TRANSIENT.includes(w.state) ||
                              (w.setup && w.setup.problem !== WorkerSetupProblem.STOPPED),
                          )
                        ? "Your Worker is being prepared. You can download Desktop while you wait."
                        : "Your account is ready. Check the Worker status below to continue."}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Dismiss"
            onClick={() =>
              session && setSession({ ...session, newAccount: false })
            }
          >
            <XIcon />
          </Button>
        </div>
      )}

      <ErrorNote error={workers.error ?? ensure.error} />

      {rows.length > 0 && billing.data && <SubscriptionNote status={billing.data} />}

      {workers.isPending || (billing.isPending && rows.length === 0) ? (
        <Loading />
      ) : workers.isError && rows.length === 0 ? (
        <Button onClick={() => void workers.refetch()}>Retry status check</Button>
      ) : rows.length === 0 && !entitled && billing.data ? (
        <div className="space-y-4">
          {paid && (
            <p data-testid="payment-confirming" className="text-sm text-muted-foreground">
              {confirmSlow
                ? "Still waiting for the payment processor to confirm your subscription. If you were charged, your payment is safe — this page keeps checking, and support@nodebase.com can help if it doesn't update within a few minutes."
                : "Payment received — confirming your subscription. This page updates automatically."}
            </p>
          )}
          {/* Hidden while a payment is being confirmed: "Subscribe" right
              after paying reads as "it didn't work, pay again". Past the
              patience it returns, because a wait with no action is worse and a
              second click cannot charge twice — the server hands back the same
              payment page while it lives and records an already-paid page
              rather than opening a new one (docs/paid-worker-signup.md). */}
          {(!paid || confirmSlow) && (
            <SubscribeCard status={billing.data} onInterest={() => void billing.refetch()} />
          )}
        </div>
      ) : rows.length === 0 ? (
        <div className="space-y-4">
          <EmptyNote>
            {ensure.isPending
              ? "Preparing your Worker. This page updates automatically."
              : `No Worker on ${session?.email} yet. Set it up to bring your private work environment online.`}
          </EmptyNote>
          <Button
            disabled={ensure.isPending}
            onClick={() => ensure.mutate({})}
          >
            <PlusIcon />{" "}
            {ensure.isPending ? "Preparing Worker…" : session?.newAccount ? "Retry Worker setup" : "Set up my Worker"}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rows.map((w) => (
            <WorkerCard key={w.id} worker={w} readAt={workers.dataUpdatedAt} />
          ))}
        </div>
      )}
      <GettingStarted />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-2 py-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function WorkerCard({ worker: w, readAt }: { worker: Worker; readAt: number }) {
  const state = w.setup
    ? w.setup.problem === WorkerSetupProblem.STOPPED
      ? "setup stopped"
      : "setting up"
    : enumLabel(WorkerState, w.state);
  const converged = w.observedConfigVersion === w.desiredConfigVersion;
  // Rendered only when the server sends a spec. It always does now, but a
  // worker record that somehow lacks one should show nothing rather than a
  // row of zeroes — "0 vCPU · 0 MB" is a claim, and a wrong one.
  const spec = w.spec
    ? `${w.spec.vcpu} vCPU · ${w.spec.memMb} MB · ${w.spec.diskGb} GB`
    : null;

  return (
    <Card className="min-w-0" data-testid="worker-card">
      <CardContent className="p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <code className="min-w-0 break-all text-sm font-medium">{w.id}</code>
          <Badge variant={stateTone(state)}>{state}</Badge>
        </div>
        {w.setup && <WorkerSetupProgress setup={w.setup} readAt={readAt} workerId={w.id} />}
        <dl className="divide-y">
          <Field label="Region">{w.region || "—"}</Field>
          {spec && <Field label="Spec">{spec}</Field>}
          <Field label="Overlay IP">
            <code>{w.overlayIp || "—"}</code>
          </Field>
          <Field label="Agent">{w.agentVersion || "—"}</Field>
          <Field label="Config">
            v{String(w.observedConfigVersion)} of v
            {String(w.desiredConfigVersion)}
            {converged ? "" : " (converging)"}
          </Field>
          <Field label="Heartbeat">{ago(w.lastHeartbeatUnix)}</Field>
        </dl>

        <WorkerPromise worker={w} />

        <SshAccess worker={w} />

        {/* No rebuild/destroy here: lifecycle is server-managed, and a button
            that ends in "contact the operator" is worse than the docs saying
            so. The admin fleet view carries the operator verbs. */}
      </CardContent>
    </Card>
  );
}

function WorkerPromise({ worker: w }: { worker: Worker }) {
  const contract = w.productContract;
  const data = contract?.data;
  const backup = w.backupStatus;
  const capabilities = contract?.capabilities;
  let storage = "Storage protection is not reported; encryption is not claimed.";
  if (
    data?.storageProtection ===
    WorkerStorageProtection.PER_WORKER_ENCRYPTED
  ) {
    storage = "Home is encrypted at rest with a separate key for this Worker.";
  } else if (
    data?.storageProtection === WorkerStorageProtection.LEGACY_HOST_STORAGE
  ) {
    storage = "Home uses legacy host storage; per-Worker encryption is not active.";
  }

  const managed = [
    capabilities?.managedServices && "services",
    capabilities?.managedCron && "cron jobs",
    capabilities?.managedSecrets && "write-only secrets",
  ].filter(Boolean).join(", ");

  return (
    <div
      data-testid="worker-product-contract"
      className="mt-4 rounded-md border bg-secondary/40 p-3 text-xs"
    >
      <p className="font-medium">What you can rely on</p>
      <ul className="mt-2 list-disc space-y-1 pl-4 text-muted-foreground">
        <li>Home survives Worker restarts and rebuilds.</li>
        <li>{storage}</li>
        {managed && <li>Nodebase restores managed {managed}.</li>}
        {backup?.independentBackup ? (
          <li>
            Platform backup is current: last copy {ago(backup.latestSuccessUnix)}, restore drill{" "}
            {ago(backup.restoreDrillUnix)}.
          </li>
        ) : backup ? (
          <li>
            Platform backup is not current ({backup.reason}); deleted data may not be recoverable through
            Nodebase.
          </li>
        ) : (
          data &&
          !data.independentBackup && (
            <li>No independent backup or snapshot yet; deleted data is not recoverable through Nodebase.</li>
          )
        )}
      </ul>
      <p className="mt-2 text-muted-foreground">
        System layer managed by Nodebase: no root, sudo, Docker, or public ports.
        Run <code>nb context --json</code> inside the Worker for the live contract.
      </p>
    </div>
  );
}

// SshAccess answers the question the old console left people to work out from
// the CLI docs: now that I have a worker, how do I get a shell on it? The
// address is the overlay IP, reachable only from a device already joined to the
// workspace, and the key is the one `nodebase login` wrote — the console never
// sees it, which is the point.
function SshAccess({ worker }: { worker: Worker }) {
  if (!worker.overlayIp) return null;
  const command = `ssh -i ~/.nodebase/id_ed25519 nodebase@${worker.overlayIp}`;
  return (
    <div className="mt-4 rounded-md border bg-muted/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          SSH access
        </p>
        <CopyButton value={command} />
      </div>
      <code
        data-testid="ssh-command"
        className="block break-all text-xs"
      >
        {command}
      </code>
      <p className="mt-2 text-xs text-muted-foreground">
        Run this from a device in this workspace — the overlay IP is not
        reachable from the public internet.
      </p>
    </div>
  );
}
